import type { BadgeVariant } from '@/shared/components/ui/badge/Badge';
import type { MotTest } from '../types/motHistory.types';

export type MotTestStatus = 'passed' | 'passed_with_advisories' | 'failed' | 'unknown';

export const NOTABLE_PASS_DEFECT_TYPES: ReadonlySet<string> = new Set([
  'ADVISORY',
  'MINOR',
  'MAJOR',
  'DANGEROUS',
  'FAIL',
]);

export interface MotTestBadge {
  status: MotTestStatus;
  label: string;
  variant: BadgeVariant;
}

type StatusInput = Pick<MotTest, 'test_result' | 'defects'>;

export function countNotableDefects(defects: MotTest['defects']): number {
  return defects.filter((d) => d.dangerous === true || NOTABLE_PASS_DEFECT_TYPES.has(d.type.toUpperCase())).length;
}

export function deriveMotTestStatus(test: StatusInput): MotTestStatus {
  const result = test.test_result.toUpperCase();
  if (result === 'FAILED') return 'failed';
  if (result === 'PASSED') return countNotableDefects(test.defects) > 0 ? 'passed_with_advisories' : 'passed';
  return 'unknown';
}

export function deriveMotTestBadge(test: StatusInput): MotTestBadge {
  const status = deriveMotTestStatus(test);
  switch (status) {
    case 'passed':
      return { status, label: 'PASSED', variant: 'success' };
    case 'passed_with_advisories':
      return {
        status,
        label: countNotableDefects(test.defects) === 1 ? 'PASSED (WITH ADVISORY)' : 'PASSED (WITH ADVISORIES)',
        variant: 'warning',
      };
    case 'failed':
      return { status, label: 'FAILED', variant: 'error' };
    default:
      return { status, label: test.test_result || 'UNKNOWN', variant: 'outline' };
  }
}
